import { CopySimple, DownloadSimple, EyeSlash, InstagramLogo, ShareNetwork, Trash } from "@phosphor-icons/react";
import { useEffect, useState } from "react";
import type { StatusResponse } from "../../shared/types";
import { getStatus, withdrawSubmission } from "../api";
import { buildInstagramPost, downloadBlob, socialCaption } from "../instagramPost";

interface Props {
  token: string;
  embedded?: boolean;
  onStatus?: (status: StatusResponse | null) => void;
}

export function PortraitSharePanel({ token, embedded = false, onStatus }: Props) {
  const [status, setStatus] = useState<StatusResponse | null>(null);
  const [postBlob, setPostBlob] = useState<Blob | null>(null);
  const [postUrl, setPostUrl] = useState<string | null>(null);
  const [shareError, setShareError] = useState<string | null>(null);
  const [captionCopied, setCaptionCopied] = useState(false);
  const [withdrawError, setWithdrawError] = useState<string | null>(null);
  const [withdrawing, setWithdrawing] = useState<"photo" | "all" | null>(null);

  useEffect(() => {
    let cancelled = false;
    let timer: number | null = null;
    const load = async () => {
      let keepPolling = true;
      try {
        const next = await getStatus(token);
        if (!cancelled) {
          setStatus(next);
          onStatus?.(next);
          keepPolling = next.state === "processing";
        }
      } catch {
        // Keep the last useful state while a temporary request is retried.
      }
      if (!cancelled && keepPolling) timer = window.setTimeout(load, 5_000);
    };
    void load();
    return () => {
      cancelled = true;
      if (timer !== null) window.clearTimeout(timer);
    };
  }, [onStatus, token]);

  useEffect(() => {
    if (!status?.name || !status.message || status.state === "rejected" || status.state === "withdrawn") return;
    let cancelled = false;
    setShareError(null);
    void buildInstagramPost(status)
      .then((blob) => {
        if (cancelled) return;
        setPostBlob(blob);
        setPostUrl((current) => {
          if (current) URL.revokeObjectURL(current);
          return URL.createObjectURL(blob);
        });
      })
      .catch(() => {
        if (!cancelled) setShareError("Your social post could not be assembled just now. Please try again.");
      });
    return () => { cancelled = true; };
  }, [status?.hashtags, status?.imageUrl, status?.message, status?.name, status?.photoDisplayState, status?.state]);

  useEffect(() => () => {
    if (postUrl) URL.revokeObjectURL(postUrl);
  }, [postUrl]);

  const sharePost = async () => {
    if (!postBlob || !status) return;
    const file = new File([postBlob], "madonna-celebration-story.jpg", { type: "image/jpeg" });
    try {
      if (navigator.share && navigator.canShare?.({ files: [file] })) {
        await navigator.share({ files: [file], text: socialCaption(status.hashtags) });
      } else {
        downloadBlob(postBlob, file.name);
      }
    } catch (error) {
      if (!(error instanceof DOMException && error.name === "AbortError")) setShareError("Sharing could not be opened. Please download the post instead.");
    }
  };

  const copyCaption = async () => {
    if (!status) return;
    try {
      await navigator.clipboard.writeText(socialCaption(status.hashtags));
      setCaptionCopied(true);
      window.setTimeout(() => setCaptionCopied(false), 2_000);
    } catch {
      setShareError("The caption could not be copied. Please select the hashtags below instead.");
    }
  };

  const withdraw = async (scope: "photo" | "all") => {
    const confirmed = window.confirm(
      scope === "photo"
        ? "Hide and permanently remove your portrait? Your text may still appear on the screens."
        : "Permanently remove your entire post, including its portrait and message, from the screens?",
    );
    if (!confirmed) return;
    setWithdrawing(scope);
    setWithdrawError(null);
    try {
      const next = await withdrawSubmission(token, scope);
      setStatus(next);
      onStatus?.(next);
      setPostBlob(null);
      setPostUrl((current) => {
        if (current) URL.revokeObjectURL(current);
        return null;
      });
    } catch {
      setWithdrawError("We could not remove it just now. Please try again.");
    } finally {
      setWithdrawing(null);
    }
  };

  if (status?.state === "withdrawn") {
    return (
      <section className={`portrait-result portrait-result--withdrawn${embedded ? " portrait-result--embedded" : ""}`} aria-live="polite">
        <span className="eyebrow">Removed</span>
        <p>
          {status.withdrawnScope === "all"
            ? "Your portrait and message have been removed from the installation."
            : "Your portrait has been removed. Your message may still appear on the screens."}
        </p>
        {status.withdrawnScope === "photo" && (
          <button className="button button--outline-light" type="button" onClick={() => void withdraw("all")} disabled={withdrawing !== null}>
            <Trash size={22} weight="bold" /> {withdrawing === "all" ? "Removing…" : "Remove entire post"}
          </button>
        )}
        <a className="button button--pink" href="/">Send one more message</a>
        {withdrawError && <p className="portrait-result__error">{withdrawError}</p>}
      </section>
    );
  }

  if (status?.state === "rejected") {
    return (
      <section className={`portrait-result portrait-result--rejected${embedded ? " portrait-result--embedded" : ""}`}>
        <p>Sorry — this one could not be published. Please try again.</p>
        <a className="button button--pink" href="/">Try again</a>
      </section>
    );
  }

  if (!status) {
    return (
      <section className={`portrait-result portrait-result--loading${embedded ? " portrait-result--embedded" : ""}`} aria-live="polite">
        <span className="eyebrow">Your story</span>
        <p>Loading your celebration story…</p>
      </section>
    );
  }

  if (!status.name || !status.message) return null;

  return (
    <section className={`portrait-result${embedded ? " portrait-result--embedded" : ""}`} aria-live="polite">
      {status.state === "processing" && status.hasPhoto && (
        <div className="processing-card processing-card--inline">
          <div className="processing-card__bars" aria-hidden="true"><i /><i /><i /><i /></div>
          <p>
            {status.photoDisplayState === "temporary"
              ? "Your moderated photo is already joining the screens and your celebration story below in a temporary black-and-white flash treatment. Within around two minutes, its AI installation look will update automatically here and on the screens."
              : status.photoDisplayState === "moderating"
                ? "Thank you. We are checking your photo for the public screens. Once it passes, it can appear in a temporary black-and-white flash treatment while the AI installation portrait is prepared."
                : "Your message is ready for the screens. If the photo cannot be used, your celebration story will continue as a text-only card."}
          </p>
        </div>
      )}
      <div className="portrait-result__social">
        <div>
          <span className="eyebrow">Ready to share</span>
          <h2>Your celebration story</h2>
        </div>
        {postUrl ? <img className="portrait-result__post" src={postUrl} alt="Your finished celebration story" /> : <div className="portrait-result__post-loading">Assembling your story…</div>}
      </div>
      <div className="status-page__actions">
        <button className="button button--pink" type="button" onClick={sharePost} disabled={!postBlob}>
          <InstagramLogo size={22} weight="bold" /> Share story
        </button>
        <button className="button button--outline-light" type="button" onClick={() => postBlob && downloadBlob(postBlob, "madonna-celebration-story.jpg")} disabled={!postBlob}>
          <DownloadSimple size={22} weight="bold" /> Download story
        </button>
        <button className="button button--outline-light" type="button" onClick={() => void copyCaption()}>
          <CopySimple size={22} weight="bold" /> {captionCopied ? "Caption copied" : "Copy caption"}
        </button>
        {status.hasPhoto && status.imageUrl && (
          <a className="button button--outline-light" href={status.imageUrl} download>
            <ShareNetwork size={22} weight="bold" /> {status.photoDisplayState === "temporary" ? "Temporary portrait" : "Portrait only"}
          </a>
        )}
        <a className="button button--outline-light" href="/">Send one more message</a>
      </div>
      <div className="portrait-result__withdrawal">
        <span className="eyebrow">Your control</span>
        <p>
          {status.hasPhoto
            ? "This private link lets you remove your portrait or your entire post from the installation."
            : "This private link lets you remove your message from the installation."}
        </p>
        <div>
          {status.hasPhoto && status.photoDisplayState !== "unavailable" && (
            <button className="button button--outline-light" type="button" onClick={() => void withdraw("photo")} disabled={withdrawing !== null}>
              <EyeSlash size={22} weight="bold" /> {withdrawing === "photo" ? "Removing…" : "Hide my photo"}
            </button>
          )}
          <button className="button button--danger-light" type="button" onClick={() => void withdraw("all")} disabled={withdrawing !== null}>
            <Trash size={22} weight="bold" /> {withdrawing === "all" ? "Removing…" : "Remove entire post"}
          </button>
        </div>
      </div>
      {shareError && <p className="portrait-result__error">{shareError}</p>}
      {withdrawError && <p className="portrait-result__error">{withdrawError}</p>}
      <p className="status-page__hashtags">{status.hashtags}</p>
    </section>
  );
}
